<script setup>
// =============================================================================
// FangateNewChatModal.vue — #143
// =============================================================================
// "Start a new chat" picker for the chat list pane's "+ New" button.
//
// Telegram realities the UX respects:
//   • Bot-Account inboxes: cold-DM is impossible. The contact must /start
//     the bot first. We surface that as a guidance card instead of an
//     empty contact list.
//   • Personal-Account (Business) inboxes: the bot can write to anyone the
//     creator has chatted with before (the business_connection grants
//     reply rights to those chats). We list those contacts.
//   • Other channels: degrade gracefully — just list known contacts.
//
// Picking a contact:
//   1. If a conversation already exists with that contact, select it.
//   2. Otherwise we can't create a brand-new conversation on Telegram from
//      Chatwoot's standard flow (the bot has to receive an incoming first),
//      so we surface a "We can't initiate from here — send Telegram-side
//      first" hint. This is the same Telegram constraint #143's task body
//      documents.
// =============================================================================

import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  open:        { type: Boolean, default: false },
  inboxId:     { type: Number,  default: null },
  inboxes:     { type: Array,   default: () => [] },
});
const emit = defineEmits(['update:open', 'select-conversation']);
const store = useStore();

const search = ref('');

const inbox = computed(() =>
  props.inboxes.find(i => i.id === props.inboxId) || null
);
// Bot vs Personal vs other. Personal-Account is the Bot-API Business mode
// — we can detect it indirectly because the conversation carries a
// business_connection_id once the partner DMs in.
const isTelegram = computed(() => inbox.value?.channel_type === 'Channel::Telegram');

// Pull every conversation we know about for this inbox from Vuex, then
// dedupe by contact. Real production usage may need a REST-side pagination
// to catch contacts whose only conversations are resolved + flushed from
// the live list — out of scope for v1.
const contacts = computed(() => {
  const all = store.getters.getAllConversations || [];
  const seen = new Map();
  for (const c of all) {
    if (c.inbox_id !== props.inboxId) continue;
    const sender = c?.meta?.sender;
    if (!sender?.id || seen.has(sender.id)) continue;
    seen.set(sender.id, {
      id: sender.id,
      name: sender.name || 'Unknown',
      thumbnail: sender.thumbnail || '',
      additional_attributes: sender.additional_attributes || {},
      conversationId: c.id,
      lastActivityAt: c.last_activity_at || c.timestamp || 0,
      hasBusinessConnection: !!c.additional_attributes?.business_connection_id,
    });
  }
  return Array.from(seen.values())
    .sort((a, b) => (b.lastActivityAt || 0) - (a.lastActivityAt || 0));
});

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return contacts.value;
  return contacts.value.filter(c =>
    c.name.toLowerCase().includes(q) ||
    (c.additional_attributes?.username || '').toLowerCase().includes(q)
  );
});

// Whether this inbox has at least one contact with business_connection_id —
// signals it's a Personal-Account flow rather than pure Bot.
const hasPersonalContacts = computed(() => contacts.value.some(c => c.hasBusinessConnection));

const close = () => emit('update:open', false);

watch(() => props.open, v => { if (v) search.value = ''; });

const pickContact = c => {
  if (c.conversationId) {
    emit('select-conversation', c.conversationId);
    close();
    return;
  }
  // No existing conversation row — bot can't cold-DM through Bot API.
  useAlert(
    'No existing conversation with this contact yet. ' +
    'Telegram bots can\'t cold-DM — ask the contact to message you first.'
  );
};

const getInitials = name => (name?.charAt(0) || '?').toUpperCase();
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fnc-overlay" @click.self="close">
      <div class="fnc-modal" @click.stop>
        <div class="fnc-head">
          <div>
            <h2 class="fnc-title">Start a new chat</h2>
            <p class="fnc-sub">
              From <strong>{{ inbox?.name || 'this inbox' }}</strong>
              <span v-if="hasPersonalContacts"> (Personal-Account)</span>
            </p>
          </div>
          <button class="fnc-close" type="button" @click="close" title="Close">×</button>
        </div>

        <!-- Bot-only inbox: no contacts → educational state -->
        <div v-if="isTelegram && contacts.length === 0" class="fnc-info">
          <strong>No contacts on this inbox yet.</strong><br>
          Telegram bots can't initiate conversations with users who haven't
          messaged the bot first. Share your bot's @username with the
          contact and ask them to send any message — it'll show up here
          immediately.
        </div>

        <!-- Search + contact list -->
        <template v-else>
          <!-- Scope hint: this picker reopens EXISTING conversations only. A cold
               first message isn't possible (Bot API) / is blocked (reply-only)
               unless an admin unlocks it. -->
          <p class="fnc-note">
            You can only pick contacts you've already chatted with — a conversation
            has to exist already. Starting a brand-new conversation isn't possible
            here unless your admin unlocks it.
          </p>
          <div class="fnc-search-wrap">
            <svg class="fnc-search-icon" width="13" height="13"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2">
              <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
            </svg>
            <input v-model="search" type="text" autofocus
                   class="fnc-search-input"
                   placeholder="Search by name or @username…" />
          </div>

          <ul v-if="filtered.length" class="fnc-list">
            <li v-for="c in filtered" :key="c.id" class="fnc-row"
                @click="pickContact(c)">
              <div class="fnc-avatar">
                <img v-if="c.thumbnail" :src="c.thumbnail" :alt="c.name" />
                <span v-else>{{ getInitials(c.name) }}</span>
              </div>
              <div class="fnc-row-main">
                <div class="fnc-row-name">{{ c.name }}</div>
                <div v-if="c.additional_attributes?.username" class="fnc-row-sub">
                  @{{ c.additional_attributes.username }}
                </div>
              </div>
              <span v-if="c.hasBusinessConnection" class="fnc-row-badge">Personal</span>
            </li>
          </ul>
          <div v-else class="fnc-empty">
            No contacts match "<strong>{{ search }}</strong>".
          </div>

          <div v-if="isTelegram && hasPersonalContacts" class="fnc-foot">
            Pick a contact to open their conversation. To message someone
            new, have them DM you first — Telegram's bot protocol requires
            the user to initiate.
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.fnc-overlay {
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(11, 20, 20, 0.45);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.fnc-modal {
  background: var(--bg-panel);
  border-radius: 14px;
  width: 100%; max-width: 480px; max-height: 80vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(20, 10, 60, 0.25);
  overflow: hidden;
}
.fnc-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-soft);
}
.fnc-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 15px; font-weight: 700;
  color: var(--ink-strong);
  margin: 0;
}
.fnc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: var(--ink-muted);
  margin: 2px 0 0;
  strong { color: var(--accent-strong); font-weight: 700; }
}
.fnc-close {
  width: 28px; height: 28px;
  border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: transparent;
  color: var(--ink-muted);
  font-size: 18px; line-height: 1;
  cursor: pointer;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}

.fnc-note {
  margin: 4px 20px 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(34, 158, 217, 0.08);
  border: 1px solid rgba(34, 158, 217, 0.22);
  color: #0d6b9c;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; line-height: 1.5;
}
.fnc-info {
  margin: 14px 20px 20px;
  padding: 14px 16px;
  border-radius: 9px;
  background: rgba(34, 158, 217, 0.10);
  border: 1px solid rgba(34, 158, 217, 0.30);
  color: #0d6b9c;
  font-family: 'Inter', sans-serif;
  font-size: 12px; line-height: 1.55;
}

.fnc-search-wrap {
  position: relative; padding: 12px 20px 8px;
}
.fnc-search-icon {
  position: absolute; left: 30px; top: 50%;
  transform: translateY(-50%);
  color: var(--ink-muted);
}
.fnc-search-input {
  width: 100%; box-sizing: border-box;
  padding: 8px 12px 8px 34px;
  border-radius: 9px;
  border: 1.5px solid var(--border-soft);
  background: var(--bg-panel);
  font-family: 'Inter', sans-serif;
  font-size: 13px; color: var(--ink-strong);
  outline: none;
  &:focus { border-color: var(--accent-ring); }
}

.fnc-list {
  list-style: none; margin: 0; padding: 4px 8px 12px;
  overflow-y: auto;
  max-height: 50vh;
  display: flex; flex-direction: column; gap: 2px;
}
.fnc-row {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 12px;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: var(--accent-tint); }
}
.fnc-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: var(--accent-tint);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Libre Baskerville', serif;
  font-size: 14px; font-weight: 700;
  color: var(--accent-strong);
  overflow: hidden;
  flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fnc-row-main {
  flex: 1; min-width: 0;
}
.fnc-row-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13.5px; font-weight: 700;
  color: var(--ink-strong);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fnc-row-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  margin-top: 1px;
}
.fnc-row-badge {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(34, 158, 217, 0.18);
  color: #0d6b9c;
  flex-shrink: 0;
}

.fnc-empty {
  padding: 18px 20px 24px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: var(--ink-muted);
}
.fnc-foot {
  padding: 10px 20px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  line-height: 1.55;
  border-top: 1px solid var(--border-soft);
}
</style>
